import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Award, FileText } from "lucide-react";
import api from "@/lib/api";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";
import { BrandCard } from "@/components/public/BrandCard";
import { DetailHeader, DetailBody } from "@/pages/public/MaterialDetailPage";

export default function BrandCertificationDetailPage() {
  const { slug } = useParams();
  const { data: cert, error, isLoading } = useQuery({
    queryKey: ["public-brand-certification", slug],
    queryFn: async () => (await api.get(`/public/brand-certifications/${slug}`)).data,
    retry: false,
  });

  const { data: certifiedBrands = [] } = useQuery({
    queryKey: ["public-brands-by-cert", cert?.id],
    queryFn: async () => (await api.get("/public/brands", { params: { cert_ids: cert.id } })).data,
    enabled: !!cert?.id,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-muted_text">Loading…</div>;
  if (error || !cert) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h1 className="font-serif text-4xl mb-4">Certification not found</h1>
        <p className="text-muted_text">This certification is either unpublished or doesn't exist.</p>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const url = `${origin}/brand-certifications/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: cert.name,
      description: cert.description?.slice(0, 280),
      url,
      sameAs: cert.external_link || undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Brand certifications", item: origin + "/brand-certifications" },
        { "@type": "ListItem", position: 3, name: cert.name, item: url },
      ],
    },
  ];

  const quickFacts = [
    cert.external_link && { label: "Official site", value: hostname(cert.external_link) },
  ].filter(Boolean);

  const sections = [
    cert.description && { icon: FileText, title: "About this certification", body: cert.description },
  ].filter(Boolean);

  const ctaPrimary = cert.external_link ? (
    <a
      href={cert.external_link}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex w-full items-center justify-center gap-2 text-xs uppercase tracking-wider bg-forest text-ivory px-4 py-2.5 hover:bg-forest/90 rounded-full"
      data-testid="brand-cert-external-link"
    >
      Visit official site <ExternalLink className="w-3.5 h-3.5" />
    </a>
  ) : null;

  const badge = cert.logo_url ? (
    <img
      src={cert.logo_url}
      alt={`${cert.name} logo`}
      className="max-h-24 w-auto object-contain"
      data-testid="brand-cert-logo"
    />
  ) : (
    <Award className="w-8 h-8 text-forest" />
  );

  return (
    <article className="bg-ivory" data-testid="brand-certification-detail-page">
      <SeoHead
        title={cert.seo_title || `${cert.name} — Brand certifications — Sustainably Curated`}
        description={cert.seo_description || cert.description?.slice(0, 160)}
        path={`/brand-certifications/${slug}`}
      />
      <JsonLd data={jsonLd} />

      <DetailHeader
        eyebrow="Brand certification"
        title={cert.name}
        badge={badge}
        accentLine="bg-forest"
        backLabel="All brand certifications"
        backTo="/brand-certifications"
      />

      <DetailBody
        sections={sections}
        quickFacts={quickFacts}
        ctaPrimary={ctaPrimary}
      />

      {certifiedBrands.length > 0 && (
        <>
          <div className="border-t border-line" />

          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8" data-testid="certified-brands-section">
            <div className="flex items-end justify-between mb-6">
              <h2 className="editorial-headline text-2xl sm:text-3xl">Certified brands</h2>
              {certifiedBrands.length > 5 && (
                <Link
                  to={`/brands?cert_ids=${cert.id}`}
                  className="text-sm text-forest underline underline-offset-4"
                  data-testid="certified-brands-view-all"
                >
                  View all →
                </Link>
              )}
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-5" data-testid="certified-brands-grid">
              {certifiedBrands.slice(0, 5).map((b) => <BrandCard key={b.id} brand={b} />)}
            </div>
          </section>
        </>
      )}
    </article>
  );
}

function hostname(u) {
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; }
}
